import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography,
    Button,
    Divider,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import DataGridCreateStrategyCampaigns from "src/components/data-grid/activations/data-grid-create-strategy-campaigns";
import { useAddKoBiddingManualCampaignMutation } from "src/context/api/activations/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";

export default function ModalCreateStrategyCampaings({
    open,
    row,
    onClose,
    setLocalPatches,
    refetch
}: {
    open: boolean;
    row: any;
    onClose: () => void;
    setLocalPatches: any;
    refetch: any
}) {
    const [updatedRow, setUpdatedRow] = useState<any[]>([]);

    const [addKoBiddingManualCampaign] = useAddKoBiddingManualCampaignMutation();

    const workspace = useCurrentWorkspace();
    const [selectedMode, setSelectedMode]: any = useState("KO Bidding Manual");


    const [validationErrors, setValidationErrors] = useState<
        Record<string, string[]>
    >({});

    const validateRows = () => {
        const errors: Record<string, string[]> = {};

        updatedRow.forEach((row) => {
            const rowErrors: string[] = [];
            const strategy = row.bidding_strategy_type;

            // =========================
            // KO BIDDING AUTO
            // =========================
            if (selectedMode === "KO Bidding Auto") {
                if (!row.max_cpc_portfolio_strategy) {
                    rowErrors.push("max_cpc_portfolio_strategy");
                }

                if (!row.accepted_variation_threshold) {
                    rowErrors.push("accepted_variation_threshold");
                }

                const autoDetail = row.activations_details?.find(
                    (d: any) => d.mode?.startsWith("KO Bidding Auto")
                );

                if (!autoDetail?.max_cpc_target_reduction) {
                    rowErrors.push("max_cpc_target_reduction");
                }

                if (
                    strategy === "Target ROAS" ||
                    strategy === "Maximize Conversion Value"
                ) {
                    if (!row.target_roas_portfolio_strategy) {
                        rowErrors.push("target_roas_portfolio_strategy");
                    }
                }

                if (rowErrors.length) {
                    errors[row.id] = rowErrors;
                }

                return;
            }

            // =========================
            // KO BIDDING MANUAL
            // =========================
            if (!row.max_cpc) {
                rowErrors.push("max_cpc");
            }

            if (strategy === "Target Impression Share") {
                if (!row.target_impression_share) {
                    rowErrors.push("target_impression_share");
                }

                if (!row.target_impression_share_position) {
                    rowErrors.push("target_impression_share_position");
                }
            }

            if (
                strategy === "Target CPA" ||
                strategy === "Maximize Conversions"
            ) {
                if (!row.target_cpa) {
                    rowErrors.push("target_cpa");
                }
            }

            if (
                strategy === "Target ROAS" ||
                strategy === "Maximize Conversion Value"
            ) {
                if (!row.target_roas) {
                    rowErrors.push("target_roas");
                }
            }

            if (rowErrors.length) {
                errors[row.id] = rowErrors;
            }
        });

        setValidationErrors(errors);

        return Object.keys(errors).length === 0;
    };

    useEffect(() => {
        if (!row || row.length === 0) return;

        const sanitized = row.map((r: any) => {
            if (!r.device || r.device.length === 0) {
                return { ...r, device: ["desktop"] };
            }
            return r;
        });

        setUpdatedRow(sanitized);
    }, [row]);

    const PORTFOLIO_TYPES = [
        "Target ROAS",
        "Maximize Conversion Value",
        "Target CPA",
        "Maximize Conversions",
    ];

    const isPortfolioStrategy = (row: any) =>
        row.platform?.toLowerCase() === "google" &&
        PORTFOLIO_TYPES.includes(row.bidding_strategy_type);

    const validateMaxCpc = (platform: string, value?: number | null) => {
        if (value == null) return null;

        const num = Number(value);

        if (platform?.toLowerCase() === "Google" && num < 0.01) {
            throw new Error("Max CPC must be at least 0.01 for Google");
        }

        if (platform?.toLowerCase() === "Bing" && num < 0.05) {
            throw new Error("Max CPC must be at least 0.05 for Bing");
        }

        return num;
    };

    const handleSubmit = async () => {
        if (!workspace) return;

        if (!validateRows()) {
            enqueueSnackbar(
                "Please fill all required fields before activating.",
                { variant: "error" }
            );

            return;
        }

        try {
            const payload = updatedRow.map((row) => {
                const base = {
                    ...row,
                    workspace_id: workspace.id,
                };

                // =========================
                // KO BIDDING AUTO
                // =========================
                if (
                    selectedMode ===
                    "KO Bidding Auto"
                ) {
                    return {
                        ...base,

                        mode:
                            "KO Bidding Auto - " +
                            (row?.strategy ??
                                "Smart Top"),

                        max_cpc: null,
                        target_roas: null,
                        target_cpa: null,

                        max_cpc_portfolio_strategy:
                            row.max_cpc_portfolio_strategy ??
                            null,

                        target_roas_portfolio_strategy:
                            row.target_roas_portfolio_strategy ??
                            null,

                        target_cpa_portfolio_strategy:
                            row.target_cpa_portfolio_strategy ??
                            null,

                        variation_threshold:
                            row.accepted_variation_threshold ??
                            null,

                        max_cpc_target_reduction:
                            row.activations_details?.find(
                                (d: any) =>
                                    d.mode?.startsWith(
                                        "KO Bidding Auto"
                                    )
                            )?.max_cpc_target_reduction ??
                            null,
                    };
                }

                // =========================
                // KO BIDDING MANUAL
                // =========================
                const validatedMaxCpc =
                    validateMaxCpc(
                        row.platform,
                        row.max_cpc
                    );

                const isPortfolio =
                    isPortfolioStrategy(row);

                if (isPortfolio) {
                    return {
                        ...base,
                        max_cpc: null,

                        max_cpc_portfolio_strategy:
                            validatedMaxCpc,

                        target_roas: null,

                        target_roas_portfolio_strategy:
                            row.target_roas ??
                            null,

                        target_cpa: null,

                        target_cpa_portfolio_strategy:
                            row.target_cpa ??
                            null,
                    };
                }

                return {
                    ...base,

                    max_cpc: validatedMaxCpc,

                    max_cpc_portfolio_strategy:
                        null,

                    target_roas_portfolio_strategy:
                        null,

                    target_cpa_portfolio_strategy:
                        null,
                };
            });

            const result =
                await addKoBiddingManualCampaign(
                    payload
                ).unwrap();

            enqueueSnackbar(`Activation added.`, {
                variant: "success",
            });

            refetch();

            updatedRow.forEach((r, index) => {
                const created = result[index];

                setLocalPatches((prev: any) => {
                    const previous = prev[r.id] ?? r;

                    const newDetails = [
                        ...(previous.activations_details?.filter(
                            (d: any) =>
                                d.mode !==
                                selectedMode
                        ) ?? []),

                        {
                            ...created,
                            status: true,
                            mode:
                                created.mode ??
                                selectedMode,

                            max_cpc:
                                created.max_cpc ??
                                r.max_cpc,
                        },
                    ];

                    return {
                        ...prev,
                        [r.id]: {
                            ...previous,
                            activations_details:
                                newDetails,
                        },
                    };
                });
            });

            onClose();
        } catch (err) {
            console.error("❌ Error:", err);

            enqueueSnackbar(
                `Failed to apply strategy`,
                { variant: "error" }
            );
        }
    };


    const isMaxCpcValid = (row: any) => {
        const value = row.max_cpc;
        if (value == null) return true;

        const num = Number(value);
        const platform = row.platform?.toLowerCase();

        if (platform === "google" && num < 0.01) return false;
        if (platform === "bing" && num < 0.05) return false;

        return true;
    };

    const isFormValid = updatedRow.every(isMaxCpcValid);

    const isEligibleForAuto = (row: any) => {
        return (
            row?.campaign_type === "Search" &&
            ["Target ROAS", "Maximize Conversion Value"].includes(
                row?.bidding_strategy_type
            )
        );
    };

    const hasEligibleAutoCampaigns = updatedRow.some(isEligibleForAuto);

    const hasActiveManualActivation = (row: any) => {
        return row?.activations_details?.some(
            (activation: any) =>
                activation?.mode === "KO Bidding Manual" &&
                activation?.status === true
        );
    };

    const hasBlockedAutoCampaigns = updatedRow.some(
        hasActiveManualActivation
    );

    return (
        <Dialog
            open={open && !!row}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: "white",
                    borderRadius: 3,
                    py: 3,
                    color: "#FFF",
                    zIndex: 99999,
                },
            }}
        >
            <DialogTitle>
                <Typography color={'rgba(51, 60, 71, 1)'} fontWeight={600} fontSize={17}>
                    Add a new activation
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Box mb={"16px"}>
                    <FormControl component="fieldset">
                        <Typography fontSize={"14px"} color={"rgba(99, 115, 129, 1)"}>Select Mode</Typography>

                        <RadioGroup
                            row
                            value={selectedMode ?? ""}
                            onChange={(e) => setSelectedMode(e.target.value)}
                            sx={{ display: "flex", gap: 2 }}
                        >
                            {[
                                "KO Bidding Manual",
                                ...(
                                    hasEligibleAutoCampaigns &&
                                        !hasBlockedAutoCampaigns
                                        ? ["KO Bidding Auto"]
                                        : []
                                ),
                            ].map((mode) => (
                                <FormControlLabel
                                    key={mode}
                                    value={mode}
                                    control={
                                        <Radio
                                            size="small"
                                            sx={{
                                                color: "rgba(145, 158, 171, 1)",
                                                "&.Mui-checked": {
                                                    color: "rgba(51, 60, 71, 1)",
                                                },
                                                "&:hover": {
                                                    backgroundColor: "rgba(200, 200, 200, 0.1)",
                                                },
                                            }}
                                        />
                                    }
                                    label={mode}
                                    sx={{
                                        color: selectedMode === mode ? "rgba(51, 60, 71, 1)" : "rgba(99, 115, 129, 1)",
                                    }}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                </Box>

                <Divider sx={{ borderColor: "rgba(145, 158, 171, 0.20))" }} />

                <Box mt={"16px"}>
                    {open && row && (
                        <DataGridCreateStrategyCampaigns
                            rows={updatedRow}
                            onChange={(newRow) => setUpdatedRow(newRow)}
                            mode={selectedMode}
                            validationErrors={validationErrors}
                        />
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button sx={{
                    backgroundColor: "rgba(145, 158, 171, 0.53)", color: "rgba(145, 158, 171, 1)",
                    "&:hover": {
                        backgroundColor: "rgba(145, 158, 171, 0.24)",
                    },
                }} onClick={onClose} variant="contained" color="inherit">
                    Cancel
                </Button>
                <Button
                    disabled={!isFormValid}
                    onClick={handleSubmit}
                    variant="contained"
                    sx={{
                        cursor: !isFormValid ? "not-allowed" : "pointer",

                        backgroundColor: "rgba(24, 24, 24, 1)",
                        "&:hover": {
                            backgroundColor: "rgba(24, 24, 24, .9)",
                        },
                        "&.Mui-disabled": {
                            backgroundColor: "rgba(24, 24, 24, 0.3)",
                            color: "rgba(255,255,255,0.4)",
                            cursor: "not-allowed",
                        },
                    }}
                >
                    Activate
                </Button>
            </DialogActions>
        </Dialog>
    );
}